/**
 * Passive network failure capture ring buffer.
 *
 * Intercepts window.fetch to record failed requests (non-2xx responses
 * and thrown errors). Preserves original fetch behavior. Entries are only
 * read when a bug report is submitted — zero cost during normal operation.
 */

interface NetworkEntry {
  timestamp: string;
  url: string;
  method: string;
  status: number | null;
  error: string | null;
}

const MAX_ENTRIES = 20;
const SENSITIVE_HEADERS = new Set(["authorization", "cookie", "x-api-key"]);

let entries: NetworkEntry[] = [];
let installed = false;
let originalFetch: typeof window.fetch | null = null;

function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    // Strip sensitive query params
    for (const key of [...parsed.searchParams.keys()]) {
      if (SENSITIVE_HEADERS.has(key.toLowerCase()) || key.toLowerCase().includes("token") || key.toLowerCase().includes("key")) {
        parsed.searchParams.set(key, "[REDACTED]");
      }
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

function pushEntry(entry: NetworkEntry): void {
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries.shift();
}

export const networkCapture = {
  install(): void {
    if (installed || typeof window === "undefined") return;
    installed = true;
    originalFetch = window.fetch;

    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      let url: string;
      let method = init?.method || "GET";
      if (typeof input === "string") {
        url = input;
      } else if (input instanceof URL) {
        url = input.toString();
      } else if (input instanceof Request) {
        url = input.url;
        if (!init?.method) method = input.method;
      } else {
        url = String(input);
      }

      try {
        const response = await originalFetch!.call(window, input, init);
        if (!response.ok) {
          pushEntry({
            timestamp: new Date().toISOString(),
            url: sanitizeUrl(url),
            method: method.toUpperCase(),
            status: response.status,
            error: response.statusText || null,
          });
        }
        return response;
      } catch (err: unknown) {
        pushEntry({
          timestamp: new Date().toISOString(),
          url: sanitizeUrl(url),
          method: method.toUpperCase(),
          status: null,
          error: err instanceof Error ? err.message : "Network error",
        });
        throw err;
      }
    };
  },

  uninstall(): void {
    if (!installed) return;
    if (originalFetch) window.fetch = originalFetch;
    originalFetch = null;
    installed = false;
  },

  getEntries(): NetworkEntry[] {
    return [...entries];
  },

  clear(): void {
    entries = [];
  },
};
